import React from 'react';
import { DRAW_PALETTE, qNo } from './gachaData';

const wrapText = (ctx, text, maxW) => {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  words.forEach((w) => {
    const t = line ? `${line} ${w}` : w;
    if (ctx.measureText(t).width > maxW && line) {
      lines.push(line);
      line = w;
    } else {
      line = t;
    }
  });
  if (line) lines.push(line);
  return lines;
};

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// 한 사람의 답변 목록을 노트 카드 이미지(PNG)로 저장
const saveImage = async (name, history) => {
  const notes = history
    .filter((h) => h.name === name)
    .map((h) => ({ q: h.q, no: qNo(h.q) }));
  if (!notes.length) return;
  try {
    await document.fonts.ready;
  } catch (e) {}
  const W = 900,
    pad = 64,
    cardPad = 36,
    scale = 2;
  const meas = document.createElement('canvas').getContext('2d');
  meas.font = '30px Caprasimo, serif';
  const wrapped = notes.map((n) => wrapText(meas, n.q, W - pad * 2 - cardPad * 2));
  const cardHs = wrapped.map((ls) => cardPad * 2 + 30 + ls.length * 42);
  const H = 210 + cardHs.reduce((a, b) => a + b + 24, 0) + pad;
  const cv = document.createElement('canvas');
  cv.width = W * scale;
  cv.height = H * scale;
  const ctx = cv.getContext('2d');
  ctx.scale(scale, scale);
  ctx.fillStyle = '#f5ead8';
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = '#56633f';
  ctx.font = '700 15px Figtree, sans-serif';
  ctx.fillText('K O R E A D Y   ·   L A N G U A G E   E X C H A N G E', pad, pad + 8);
  ctx.fillStyle = '#201e1d';
  ctx.font = '44px Caprasimo, serif';
  ctx.fillText(`${name}'s questions`, pad, pad + 66);
  ctx.fillStyle = '#645c50';
  ctx.font = '16px Figtree, sans-serif';
  ctx.fillText(
    `${new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })}  ·  ${notes.length}${notes.length === 1 ? ' note' : ' notes'}`,
    pad,
    pad + 100,
  );
  let y = 210;
  wrapped.forEach((ls, i) => {
    const h = cardHs[i];
    ctx.save();
    ctx.shadowColor = 'rgba(46,43,37,.14)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 6;
    ctx.fillStyle = '#f9f4ed';
    roundRect(ctx, pad, y, W - pad * 2, h, 24);
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = '#8c491a';
    ctx.font = '700 13px Figtree, sans-serif';
    ctx.fillText(`N O .  ${notes[i].no}`, pad + cardPad, y + cardPad + 4);
    ctx.fillStyle = '#201e1d';
    ctx.font = '30px Caprasimo, serif';
    ls.forEach((line, j) =>
      ctx.fillText(line, pad + cardPad, y + cardPad + 44 + j * 42),
    );
    y += h + 24;
  });
  const a = document.createElement('a');
  a.download = `koready-questions-${name
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/gi, '-')}.png`;
  a.href = cv.toDataURL('image/png');
  a.click();
};

const HistoryPanel = ({
  names,
  history,
  person,
  onPickPerson,
  onBack,
  onClose,
  onReset,
}) => {
  const counts = {};
  history.forEach((h) => {
    counts[h.name] = (counts[h.name] || 0) + 1;
  });
  const personNotes = history
    .filter((h) => h.name === person)
    .map((h, i) => ({ q: h.q, no: qNo(h.q), rot: i % 2 ? 0.8 : -0.8 }));

  return (
    <div
      onClick={onClose}
      className='ibg-overlay'
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(46, 43, 37, 0.45)',
        backdropFilter: 'blur(12px)',
        padding: 'clamp(14px, 4vw, 32px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
        style={{
          width: 'min(520px, 94vw)',
          maxHeight: '82vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--c-bg)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 34px 90px rgba(46, 43, 37, 0.4)',
          overflow: 'hidden',
          animation: 'ibgPanelIn 0.45s cubic-bezier(0.2, 1.1, 0.3, 1) both',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '24px 28px 14px',
          }}
        >
          <div>
            <div style={{ fontFamily: 'var(--f-heading)', fontSize: 25 }}>
              The question box
            </div>
            <div style={{ fontSize: 13, color: 'var(--c-n-600)', marginTop: 4 }}>
              {history.length
                ? 'Tap a name to revisit their questions.'
                : 'Answered notes will land here.'}
            </div>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='ibg-close'
            aria-label='Close'
            style={{ width: 36, height: 36, fontSize: 16, flexShrink: 0 }}
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 22px 22px' }}>
          {person ? (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 10,
                  paddingBottom: 12,
                }}
              >
                <button
                  type='button'
                  onClick={onBack}
                  style={{
                    border: 'none',
                    cursor: 'pointer',
                    background: 'transparent',
                    color: 'var(--c-accent-700)',
                    fontWeight: 700,
                    fontSize: 13,
                    padding: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  ← Everyone
                </button>
                {personNotes.length > 0 && (
                  <button
                    type='button'
                    onClick={() => saveImage(person, history)}
                    className='ibg-btn-warm'
                    style={{
                      fontSize: 13,
                      padding: '10px 18px',
                      boxShadow: '0 4px 10px rgba(140, 73, 26, 0.3)',
                    }}
                  >
                    Save as image
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {personNotes.map((h, i) => (
                  <div
                    key={i}
                    style={{
                      background: 'var(--c-n-100)',
                      borderRadius: 'var(--radius-md)',
                      padding: '18px 22px',
                      boxShadow: '0 6px 16px rgba(90, 60, 40, 0.1)',
                      transform: `rotate(${h.rot}deg)`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.22em',
                        color: 'var(--c-warm-700)',
                        marginBottom: 8,
                      }}
                    >
                      NO. {h.no}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--f-heading)',
                        fontSize: 18,
                        lineHeight: 1.4,
                        color: 'var(--c-text)',
                      }}
                    >
                      {h.q}
                    </div>
                  </div>
                ))}
              </div>
              {personNotes.length === 0 && (
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--c-n-600)',
                    textAlign: 'center',
                    padding: '14px 0',
                  }}
                >
                  No answered questions yet.
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {names.map((n, i) => (
                <div
                  key={n}
                  onClick={() => onPickPerson(n)}
                  className='ibg-person-row'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    background: 'var(--c-n-100)',
                    borderRadius: 999,
                    padding: '10px 20px 10px 12px',
                    boxShadow: '0 4px 10px rgba(90, 60, 40, 0.08)',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: `linear-gradient(180deg, ${DRAW_PALETTE[i % DRAW_PALETTE.length]} 0 52%, var(--c-n-100) 52%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#fff',
                      textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                      boxShadow: 'inset -2px -4px 7px rgba(71, 66, 56, 0.25)',
                      flexShrink: 0,
                    }}
                  >
                    {(n[0] || '?').toUpperCase()}
                  </div>
                  <div style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>
                    {n}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--c-n-600)' }}>
                    {counts[n] || 0}
                    {(counts[n] || 0) === 1 ? ' note' : ' notes'}
                  </div>
                  <div style={{ color: 'var(--c-accent-600)', fontSize: 16 }}>
                    ›
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '12px 24px 20px',
            textAlign: 'center',
            borderTop: '1px solid var(--c-divider)',
          }}
        >
          <button
            type='button'
            onClick={onReset}
            className='ibg-ghost-link'
            style={{ fontSize: 13 }}
          >
            Start a new session
          </button>
        </div>
      </div>
    </div>
  );
};

export default HistoryPanel;
